<template>
	<div class="factor-searcher">
		<input
			ref="input"
			type="text"
			placeholder="성분명을 입력하세요"
			class="factor-searcher__input"
			@keyup.down.prevent="selectByArrow(0)"
			@input="factorSearch"
		/>
		<ul ref="list" class="factor-searcher__factor-list">
			<template v-if="formatList.length">
				<li v-for="item in formatList" :key="item.id" ref="listItem">
					<button
						ref="listBtn"
						:title="item.value"
						class="factor-searcher__factor-item-button"
						v-text="item.value"
						@keydown.up.prevent="selectByArrow($event.target, 'up')"
						@keydown.down.prevent="
							selectByArrow($event.target, 'down')
						"
						@click="factorSelect(item)"
					/>
				</li>
			</template>
			<template v-else-if="activeBatch && formatBatchList.length">
				<li
					v-for="item in formatBatchList"
					:key="item.id"
					:class="[
						'factor-searcher__factor-batch-item',
						{
							'factor-searcher__factor-batch-item--not-found':
								item.notFound,
						},
					]"
				>
					<label class="factor-searcher__factor-batch-item-label">
						<input
							:value="item.id"
							:checked="item.checked"
							type="checkbox"
							:disabled="item.notFound"
							class="factor-searcher__factor-batch-item-checkbox"
							@input="
								checkFactorBatchItem(item.id, !item.checked)
							"
						/>
						<span
							class="factor-searcher__factor-batch-item-text"
							v-text="item.value"
						/>
					</label>
					<div class="factor-searcher__factor-batch-right-item">
						<button
							v-if="item.notFound"
							class="factor-searcher__replace-button btn small"
							v-text="'성분대체'"
							@click="openReplaceFactorModal(item.value)"
						/>
						<button
							class="btn small red"
							v-text="'성분삭제'"
							@click="deleteFactorBatchItem(item.id, item.value)"
						/>
					</div>
				</li>
			</template>
		</ul>
		<div
			v-if="formatBatchList.length"
			class="factor-searcher__batch-button-wrapper"
		>
			<button
				class="btn factor-searcher__batch-button"
				v-text="'일괄적용'"
				@click="addAllBatchList"
			/>
			<button
				class="btn factor-searcher__batch-button"
				v-text="'선택적용'"
				@click="addSelectedBatchList"
			/>
			<button
				class="btn red factor-searcher__batch-button"
				v-text="'선택삭제'"
				@click="deleteSelectedBatchList"
			/>
		</div>
		<ReplaceFactorModal
			v-if="replaceTarget"
			@closeModal="replaceFactorModalClose"
			@replaceFactor="replaceFactor"
		/>
	</div>
</template>
<script>
	import debounce from 'lodash/debounce';
	import compact from 'lodash/compact';
	import uniq from 'lodash/uniq';
	import ReplaceFactorModal from '@/components/product/ReplaceFactorModal.vue';

	export default {
		name: 'FactorSearcher',
		components: {
			ReplaceFactorModal,
		},
		props: {
			activeBatch: {
				type: Boolean,
				default: true,
			},
			selectedList: {
				type: Array,
				default: () => [],
			},
		},
		data() {
			return {
				factorList: [],
				factorBatchList: [],
				replaceTarget: false,
			};
		},
		computed: {
			formatList() {
				return this.factorList.reduce((acc, cur) => {
					if (
						this.selectedList.every(
							selected => selected.id !== cur.factorIdx
						)
					) {
						acc.push({
							id: cur.factorIdx,
							value: `${cur.factorKorean}(${cur.factorEnglish})`,
						});
					}
					return acc;
				}, []);
			},
			formatBatchList() {
				return this.factorBatchList.filter(item =>
					this.selectedList.every(selected => selected.id !== item.id)
				);
			},
		},
		methods: {
			selectByArrow(target, type) {
				const listItem = this.$refs.listItem;
				const targetLi =
					target?.parentElement?.[
						type === 'up'
							? 'previousElementSibling'
							: 'nextElementSibling'
					];
				if (
					listItem &&
					listItem.length &&
					typeof target !== 'number' &&
					targetLi
				) {
					targetLi.firstChild.focus();
				} else {
					this.$refs.input.focus();
					if (
						listItem &&
						listItem.length &&
						typeof target === 'number'
					) {
						setTimeout(() => {
							listItem[target].firstChild.focus();
						});
					}
				}
			},
			async singleSearch(keyword) {
				const params = { keyword };
				const path = '/api/products/factor';
				const {
					data: { data },
				} = await this.$axiosAdmin2.get(path, { params });
				this.factorBatchList = [];
				this.factorList = data || [];
			},
			async multiSearch(searchWords) {
				const { data } = await this.$axiosAdmin2.post(
					'/api/products/factors_multi',
					searchWords
				);
				this.factorList = [];
				this.factorBatchList = uniq(searchWords.split(',')).map(
					key => ({
						id: data[key]?.factorIdx || key,
						value: data[key]?.factorIdx
							? `${data[key].factorKorean}(${data[key].factorEnglish})`
							: key,
						notFound: !data[key],
						checked: !!data[key],
					})
				);
			},
			factorSearch: debounce(async function ({ target }) {
				const value = target.value;
				if (value === '') {
					this.factorList = [];
					this.factorBatchList = [];
					return;
				}
				const arrValue = compact(
					value.split(',').map(value => value.trim())
				);
				if (arrValue.length === 1 || !this.activeBatch) {
					this.singleSearch(arrValue[0]);
				} else if (arrValue.length > 1) {
					this.multiSearch(arrValue.join(','));
				}
			}, 500),
			factorSelect({ id, value }) {
				const selectedItem = { id, value };
				this.$refs.input.value = '';
				this.$refs.input.focus();
				this.factorList = [];
				this.$emit('addFactor', [selectedItem]);
			},
			checkFactorBatchItem(id, checked) {
				this.factorBatchList = this.factorBatchList.map(item => ({
					...item,
					checked: item.id === id ? checked : item.checked,
				}));
			},
			openReplaceFactorModal(value) {
				this.replaceTarget = value;
			},
			deleteFactorBatchItem(id, name) {
				if (!confirm(`목록에서 "${name}" 삭제합니까?`)) return;
				this.factorBatchList = this.factorBatchList.filter(
					item => item.id !== id
				);
			},
			addAllBatchList() {
				if (this.formatBatchList.every(item => item.notFound)) {
					alert('매칭된 성분이 없습니다.');
					return;
				}
				if (!confirm('매칭된 모든 성분을 추가목록에 추가 하겠습니까?'))
					return;
				this.$emit(
					'addFactor',
					this.formatBatchList.filter(item => !item.notFound)
				);
			},
			addSelectedBatchList() {
				if (this.formatBatchList.every(item => !item.checked)) {
					alert('선택된 성분이 없습니다.');
					return;
				}
				if (!confirm('선택된 성분만 추가목록에 추가 하겠습니까?'))
					return;
				this.$emit(
					'addFactor',
					this.formatBatchList.filter(item => item.checked)
				);
			},
			deleteSelectedBatchList() {
				if (this.formatBatchList.every(item => !item.checked)) {
					alert('선택된 성분이 없습니다.');
					return;
				}
				if (!confirm('선택된 성분을 검색목록에서 삭제 하겠습니까?'))
					return;
				this.factorBatchList = this.factorBatchList.filter(
					item => !item.checked
				);
			},
			replaceFactorModalClose() {
				this.replaceTarget = false;
			},
			replaceFactor([{ id, value }]) {
				if (this.selectedList.some(item => item.id === id)) {
					alert(`이미 추가목록에 추가된 성분 입니다. "${value}"`);
					return;
				}
				if (this.formatBatchList.some(item => item.id === id)) {
					alert(`이미 검색목록에 추가된 성분 입니다. "${value}"`);
					return;
				}
				this.factorBatchList = this.factorBatchList.map(item =>
					item.value === this.replaceTarget
						? {
								id,
								value,
								notFound: false,
								checked: true,
							}
						: item
				);
				this.replaceTarget = false;
			},
		},
		async mounted() {
			await this.$nextTick();
			this.$refs.input.focus();
		},
	};
</script>
<style lang="scss" scoped>
	.factor-searcher {
		&__input {
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
		}

		&__factor-list {
			overflow-x: hidden;
			overflow-y: auto;
			width: 100%;
			height: 475px;
			border: solid 1px #dfe6ee;
			border-top: 0;
			border-bottom-left-radius: 4px;
			border-bottom-right-radius: 4px;
		}

		&__factor-item-button {
			overflow: hidden;
			cursor: pointer;
			display: block;
			width: 100%;
			padding: 8px;
			white-space: nowrap;
			text-overflow: ellipsis;
			text-align: left;

			&:hover,
			&:focus {
				background: #f4f7fa;
			}
		}

		&__factor-batch-item {
			display: flex;
			width: 100%;
			padding: 8px;
			justify-content: space-between;
			align-items: center;
		}

		&__factor-batch-item-label {
			overflow: hidden;
			cursor: pointer;
			display: flex;
			height: 20px;
			align-items: center;
		}

		&__factor-batch-item-checkbox {
			cursor: pointer;
			display: block;
			margin-right: 8px;
		}

		&__factor-batch-item-text {
			display: block;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
		}

		&__factor-batch-right-item {
			display: flex;
			margin-left: 8px;
		}

		&__replace-button {
			flex-shrink: 0;
			display: block;
			margin-right: 8px;
		}

		&__batch-button-wrapper {
			display: flex;
			width: 100%;
			margin-top: 18px;
			flex-wrap: nowrap;
			justify-content: flex-end;
		}

		&__batch-button {
			margin-left: 8px;
		}

		&__batch-button:first-child {
			margin-left: 0;
		}

		&__factor-batch-item--not-found {
			background: rgba(red, 0.4);

			.factor-searcher__factor-batch-item-label,
			.factor-searcher__factor-batch-item-checkbox {
				cursor: not-allowed;
			}
		}
	}
</style>
